// ABOUTME: Shared chart data normalization utilities for report rendering.
// ABOUTME: Extracts common chart data/type transformations used across SharedReportPage, ReportPrintPage, and ChartSection.

import type { VisualizationConfig } from '@/types/api';
import type { DataPoint } from '@/types';

type NormalizedChartType = 'bar' | 'line' | 'area' | 'pie';

/**
 * Normalizes visualization data into a consistent DataPoint array.
 * Handles missing/null data gracefully by returning an empty array.
 */
export function normalizeChartData(viz: VisualizationConfig): DataPoint[] {
  if (!viz.data || !Array.isArray(viz.data)) {
    return [];
  }

  return viz.data.map((d) => ({
    label: String(d.label || d.name || ''),
    value: Number(d.value || 0),
    prev: d.prev as number | undefined,
  }));
}

/**
 * Normalizes chart type strings to the subset supported by ReportChart.
 * Converts compound types (stacked-bar, grouped-bar) to their base type (bar).
 * Falls back to 'bar' for unknown types.
 */
export function normalizeChartType(type: string | undefined): NormalizedChartType {
  if (!type) {
    return 'bar';
  }

  if (type === 'stacked-bar' || type === 'grouped-bar') {
    return 'bar';
  }

  const validTypes: NormalizedChartType[] = ['bar', 'line', 'area', 'pie'];
  if (validTypes.includes(type as NormalizedChartType)) {
    return type as NormalizedChartType;
  }

  return 'bar';
}

/**
 * Resolves the chart type from a VisualizationConfig, checking both
 * config.type and chart_type fields with appropriate priority.
 */
export function resolveChartType(viz: VisualizationConfig): NormalizedChartType {
  const rawType = (viz.config?.type as string) || viz.chart_type || 'bar';
  return normalizeChartType(rawType);
}
